import { Button, Empty, Popover, Select, Tooltip } from '../index';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
import React, { ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';

import './tagsearch.scss';

export interface TagOption {
    value: string;
    label: React.ReactNode;
}

interface TagSearchContentProps<Option extends TagValue> {
    tags: Option[];
    options: TagOption[];
    onSearch: (tag: string) => void;
    onFetchTags: (tags: Option[]) => Promise<TagOption[]>;
    onChange?: (tags: Option[]) => Option[];
    onAdd: (tags: Option[]) => void;
    onCancel: () => void;
    dataTestId?: string;
    tagRender?: (props: CustomTagProps) => ReactElement;
}

export interface TagValue {
    key?: string;
    label: React.ReactNode;
    value: string;
}

const TagSearchContent = <Option extends TagValue>({
    tags,
    options,
    onSearch,
    onFetchTags,
    onChange,
    onAdd,
    onCancel,
    dataTestId,
    tagRender,
}: TagSearchContentProps<Option>) => {
    const [selectedTags, setSelectedTags] = useState<Option[]>(tags);
    const [values, setValues] = useState<TagOption[]>([]);

    const onSearchHandler = React.useMemo(() => {
        return debounce(onSearch, 500);
    }, [onSearch]);

    const onFocusHandler = React.useCallback(() => onSearch(''), [onSearch]);

    const onChangeHandler = (tags: Option[]) => {
        if (onChange) {
            setSelectedTags(onChange(tags));
        } else {
            setSelectedTags(tags);
        }
    };

    const onCancelHandler = () => {
        onCancel();
    };

    const onAddHandler = () => {
        onAdd(selectedTags);
    };

    useEffect(() => {
        const fetchValues = async () => {
            const newValues = selectedTags.length > 0 ? await onFetchTags(selectedTags) : [];
            setValues(newValues);
        };
        fetchValues();
    }, [selectedTags, onFetchTags]);

    useEffect(() => {
        setSelectedTags(tags);
    }, [tags]);

    return (
        <>
            <div className="rain-tag-search-header">
                <div className="rain-tag-search-header-title">
                    <span>Filter by Tags</span>
                    <Tooltip
                        overlayClassName="rain-tag-search-tooltip"
                        title="Add one or more tags to find teammates or rooms who have all matching tags. If you’re a boolean pro, this is an AND relationship.">
                        <span>
                            <FontAwesomeIcon icon={faQuestionCircle} />
                        </span>
                    </Tooltip>
                </div>
                <Select<TagValue[]>
                    labelInValue
                    mode="multiple"
                    placeholder="Type tag names"
                    autoFocus={true}
                    options={options}
                    onFocus={onFocusHandler}
                    onSearch={onSearchHandler}
                    onChange={onChangeHandler}
                    filterOption={false}
                    value={selectedTags}
                    data-testid={dataTestId}
                    tagRender={tagRender}
                />
            </div>
            <div className="rain-tag-search-body">
                <span className="rain-tag-search-result-text">{`Showing ${values.length} Total Filtered Results`}</span>
                <div
                    className={classnames(
                        'rain-tag-search-body-content',
                        !values.length && 'rain-tag-search-body-content-empty'
                    )}>
                    {values.length ? values.map((value) => <div key={value.value}>{value.label}</div>) : <Empty />}
                </div>
            </div>
            <div className="rain-tag-search-footer">
                <Button onClick={onCancelHandler}>Cancel</Button>
                <Button type="primary" onClick={onAddHandler}>
                    Add Tags
                </Button>
            </div>
        </>
    );
};

interface TagSearchProps<Option extends TagValue> extends TagSearchContentProps<Option> {
    isVisible: boolean;
    dataTestId?: string;
    zIndex?: number;
    autoAdjustOverflow?: boolean;
    children?: React.ReactNode;
}

const TagSearch = <Option extends TagValue = TagValue>({
    isVisible,
    children,
    zIndex,
    autoAdjustOverflow = false,
    ...contentProps
}: TagSearchProps<Option>) => {
    return (
        <Popover
            visible={isVisible}
            overlayClassName="rain-tag-search"
            placement="bottom"
            content={<TagSearchContent {...contentProps} />}
            trigger="click"
            autoAdjustOverflow={autoAdjustOverflow}
            zIndex={zIndex}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}>
            {children}
        </Popover>
    );
};

export default TagSearch;
